import { PageShell } from './PageShell.js';
import type { WashedLocale } from '@washed/i18n';
import styles from './AboutPage.module.css';

interface PageProps {
  locale: WashedLocale;
  onLocaleChange: (locale: WashedLocale) => void;
}

const TITLE: Record<WashedLocale, string> = {
  fr: 'À propos de Washed — la plateforme derrière la laveuse à domicile',
  en: 'About Washed — the platform behind in-home laundry',
};

export function AboutPage({ locale, onLocaleChange }: PageProps) {
  const fr = locale === 'fr';

  const stack = fr
    ? [
        {
          title: 'Abonné',
          body: 'Choix de formule, planning, paiement Mobile Money, suivi des visites, réclamations et continuité de la relation.',
        },
        {
          title: 'Laveuse',
          body: 'Affectations, créneaux, check-in et check-out, preuve photo et informations nécessaires à chaque visite.',
        },
        {
          title: 'Bureau',
          body: 'Affectations, capacité, paiements, support, litiges et supervision opérationnelle depuis une console dédiée.',
        },
      ]
    : [
        {
          title: 'Subscriber',
          body: 'Plan choice, scheduling, mobile-money payment, visit tracking, support and continuity of the service relationship.',
        },
        {
          title: 'Washerwoman',
          body: 'Assignments, time windows, check-in and check-out, photo evidence and the information needed for each visit.',
        },
        {
          title: 'Bureau',
          body: 'Assignments, capacity, payments, support, disputes and operational supervision from a dedicated console.',
        },
      ];

  return (
    <PageShell
      locale={locale}
      onLocaleChange={onLocaleChange}
      title={TITLE[locale]}
      heroHeader={false}
      childOwnsH1
    >
      <div className={styles.about}>
        <section className={styles.hero}>
          <div>
            <p className={styles.eyebrow}>
              {fr ? 'Lomé · construit pour le terrain' : 'Lomé · built for the real world'}
            </p>
            <h1 className={styles.h1} aria-label={TITLE[locale]}>
              {fr ? (
                <>
                  Nous n’avons pas inventé la laveuse à domicile. Nous avons construit le{' '}
                  <em>système.</em>
                </>
              ) : (
                <>
                  We did not invent in-home laundry. We built the <em>system.</em>
                </>
              )}
            </h1>
          </div>
          <p className={styles.heroCopy}>
            {fr
              ? 'À Lomé, faire venir une laveuse chez soi existe déjà. Le problème est ailleurs : trouver la bonne personne, savoir si elle viendra, garder une relation régulière, organiser le paiement et avoir un recours quand quelque chose se passe mal. Washed structure cette relation sans changer le geste.'
              : 'In Lomé, inviting a washerwoman into the home already exists. The problem is everything around it: finding the right person, knowing she will come, keeping the relationship consistent, handling payment and having support when something goes wrong. Washed structures that relationship without changing the underlying behaviour.'}
          </p>
        </section>

        <section className={styles.principle}>
          <span className={styles.principleLabel}>{fr ? 'Notre principe' : 'Our principle'}</span>
          <blockquote>
            {fr
              ? 'La technologie doit rendre le service plus fiable — pas rendre une relation humaine plus compliquée.'
              : 'Technology should make the service more reliable — not make a human relationship more complicated.'}
          </blockquote>
        </section>

        <section>
          <div className={styles.sectionHead}>
            <span>{fr ? 'Produit' : 'Product'}</span>
            <h2>{fr ? 'Trois surfaces. Un seul service.' : 'Three surfaces. One service.'}</h2>
          </div>
          <div className={styles.stack}>
            {stack.map((item, index) => (
              <article className={styles.stackCard} key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section>
          <div className={styles.sectionHead}>
            <span>{fr ? 'Recherche' : 'Research'}</span>
            <h2>{fr ? 'Commencer par écouter, puis tester.' : 'Listen first. Then test.'}</h2>
          </div>
          <div className={styles.evidenceGrid}>
            <article className={styles.metricCard}>
              <span className={styles.metricLabel}>
                {fr
                  ? 'Étude exploratoire · Togo · mai 2026'
                  : 'Exploratory study · Togo · May 2026'}
              </span>
              <strong className={styles.metricValue}>63</strong>
              <p>
                {fr
                  ? 'personnes ont répondu à notre enquête initiale sur les habitudes de lavage, le prix et l’intérêt pour le service. Ces données orientent le bêta ; elles ne sont pas présentées comme une preuve de traction commerciale.'
                  : 'people answered our initial survey on laundry habits, pricing and interest in the service. The data informs the beta; it is not presented as proof of commercial traction.'}
              </p>
            </article>
            <article className={styles.researchCard}>
              <span className={styles.metricLabel}>{fr ? 'Stade actuel' : 'Current stage'}</span>
              <h3>
                {fr
                  ? 'Produit construit. Hypothèses à mesurer.'
                  : 'Product built. Assumptions to measure.'}
              </h3>
              <p>
                {fr
                  ? 'La prochaine étape est simple : mesurer la conversion payante, la ponctualité des visites, la satisfaction, la rétention, le coût réel par visite et la capacité d’une laveuse. Le produit est conçu pour enregistrer ces signaux dès le bêta.'
                  : 'The next step is straightforward: measure paid conversion, visit reliability, satisfaction, retention, true cost per visit and washerwoman capacity. The product is designed to capture those signals from the beta onward.'}
              </p>
            </article>
          </div>
        </section>

        <section className={styles.founder}>
          <div className={styles.founderMark} aria-hidden="true">
            RT
          </div>
          <div className={styles.founderCopy}>
            <span>{fr ? 'Fondateur' : 'Founder'}</span>
            <h2>Ryan Tomegah</h2>
            <p>
              {fr
                ? 'Washed part d’une idée simple : organiser un service qui existe déjà au lieu d’imposer un nouveau comportement. Le produit, les opérations et l’économie du service sont développés ensemble, parce qu’une belle application ne vaut rien si la visite réelle ne fonctionne pas.'
                : 'Washed starts from a simple idea: organize a service that already exists instead of forcing a new behaviour. Product, operations and service economics are developed together, because a beautiful app is worthless if the real-world visit does not work.'}
            </p>
            <div className={styles.founderMeta}>
              <b>{fr ? 'Produit' : 'Product'}</b>
              <b>{fr ? 'Opérations' : 'Operations'}</b>
              <b>Lomé</b>
            </div>
          </div>
        </section>
      </div>
    </PageShell>
  );
}
